/**
 * Customer facts of a workflow (M29 PDI-006 part 2): the tier, standing and attributes the operator pushed for the
 * person whose verified session a run belongs to. Mirrors backend `app/core/constants/workflow_customer.py`.
 * A run whose conversation was never signed in has every one of them empty; the reference is never a fact.
 */
import { ACCOUNT_STATUS_VALUES } from "./customer-context";

export const CUSTOMER_FACT_PREFIX = "customer.";
export const CUSTOMER_ATTR_PREFIX = "customer.attr.";

/** Condition fields of the picker (backend `CUSTOMER_CONDITION_FIELDS`). */
export const CUSTOMER_CONDITION_FIELDS = {
  "customer.tier": "text",
  "customer.account_status": "text",
  "customer.verified_by": "text",
  "customer.context_as_of": "text",
} as const;

/** Who vouched for the context: the operator's system, or our staff. */
export const CUSTOMER_VERIFIED_BY_VALUES = ["operator", "staff"] as const;

/** Customer facts with a short list of values (the editor offers them as choices). */
export const CUSTOMER_FIELD_CHOICES: Readonly<Record<string, readonly string[]>> = {
  "customer.account_status": ACCOUNT_STATUS_VALUES,
  "customer.verified_by": CUSTOMER_VERIFIED_BY_VALUES,
};

/** `customer.attr.<key>`: the operator's attributes, an open set like the run variables (backend `CUSTOMER_ATTRIBUTE_FIELD`). */
export const CUSTOMER_ATTRIBUTE_FIELD_PATTERN = /^customer\.attr\.[a-z][a-z0-9_]{0,39}$/;

/** The attribute key of a `customer.attr.<key>` field, or null. */
export function customerAttributeKey(field: string): string | null {
  return CUSTOMER_ATTRIBUTE_FIELD_PATTERN.test(field) ? field.slice(CUSTOMER_ATTR_PREFIX.length) : null;
}

/** `customer.attr.<key>` for an attribute key. */
export const customerAttributeField = (key: string): string => `${CUSTOMER_ATTR_PREFIX}${key}`;

/** The operation of the "Get customer support profile" preset and what it fills (backend `PROFILE_*`). */
export const SUPPORT_PROFILE_OPERATION_KEY = "get_customer_support_profile";
export const PROFILE_FIELD_MAX_AGE_S = { account_status: 60, kyc_status: 60, tier: 900, brand: 900, locale: 900 } as const;
